import { debugRun } from '../debug/debug.utils';
import {
    CountryProfile,
    CountryTaxonomy,
    Taxonomies,
    Taxonomy
} from '../types';

export const getTaxonomyById = <
    T extends Taxonomy
>(
    taxonomyList: T[],
    taxonomyId: number
) => (
    taxonomyList
        .find(taxonomy => taxonomy.id === taxonomyId)
    || null
);

export const getRegionTaxonomyByCountryId = (
    taxonomies: Taxonomies,
    countryId: number
) => {
    const countryTaxonomy = getTaxonomyById(taxonomies.countries, countryId);

    if (countryTaxonomy) {
        return getTaxonomyById(taxonomies.regions, countryTaxonomy.regionId);
    }

    return null;
};

export const getTaxonomyName = (
    taxonomies: Taxonomies,
    taxonomyType: keyof Taxonomies,
    taxonomyId: number
): string => {
    if (taxonomyId < 1) {
        // Invalid id
        return ``;
    }

    for (let taxonomy of taxonomies[taxonomyType]) {
        if (taxonomy.id === taxonomyId) {
            // success
            return taxonomy.name;
        }
    }

    // unknown taxonomy type
    return ``;
};

export const getCountryTaxonomyListByRegionIdList = (
    countryTaxonomyList: CountryTaxonomy[],
    regionIdList: number[]
) => (
    countryTaxonomyList
        .filter(country => regionIdList.includes(country.regionId))
)

export const getTaxonomyNameList = (
    taxonomies: Taxonomies,
    taxonomyType: keyof Taxonomies,
    taxonomyIds: number[]
): string[] => taxonomyIds
    .map(taxonomyId => getTaxonomyName(taxonomies, taxonomyType, taxonomyId));

export const getDefaultSelectedCountryId = (
    profiles: CountryProfile[]
): number | null => {
    if (profiles.length) {
        return profiles[0].countryId;
    }

    return null;
};

export const filterCountryTaxonomiesFromCountryProfiles = (
    countryTaxonomies: Taxonomy[],
    countryProfiles: CountryProfile[],
    selectedCountryIdsFromFilters: number[] = [],
) => countryTaxonomies.filter(countryTaxonomy => (
    countryProfiles.some(countryProfile => (
        countryProfile.countryId === countryTaxonomy.id
    ))
    ||
    selectedCountryIdsFromFilters.indexOf(countryTaxonomy.id) !== -1
));

interface ExtendTaxonomiesAcc {
    localLevel: {[countryId: number]: boolean};
    nationalLevel: {[countryId: number]: boolean};
    countryRegionGroups: {
        [countryId: number]: {
            [regionId: number]: number;
        };
    };
}

export const extendTaxonomies = (
    taxonomies: Taxonomies,
    countryProfiles: CountryProfile[],
) => {
    const profileIds = countryProfiles.reduce<ExtendTaxonomiesAcc>((acc, profile) => {
        const { localLevel, countryId, regionId } = profile;

        if (localLevel && !acc.localLevel[countryId]) {
            acc.localLevel[countryId] = true;
        }
        else if (!localLevel && !acc.nationalLevel[countryId]) {
            acc.nationalLevel[countryId] = true;
        }

        if (Boolean(regionId)) {
            if (!acc.countryRegionGroups[countryId]) {
                acc.countryRegionGroups[countryId] = {};
            }

            if (!acc.countryRegionGroups[countryId][regionId]) {
                acc.countryRegionGroups[countryId][regionId] = 1;
            }
            else {
                acc.countryRegionGroups[countryId][regionId] += 1;
            }
        }

        return acc;
    }, {
        localLevel: {},
        nationalLevel: {},
        countryRegionGroups: {},
    });

    taxonomies.countries = taxonomies.countries.map(countryTaxonomy => {
        const regionsForThisCountry = profileIds.countryRegionGroups[countryTaxonomy.id] || [];
        const regionIds = Object.keys(regionsForThisCountry).map(Number);
        
        let regionId = 0;

        if (regionIds.length === 1) {
            regionId = regionIds[0];
        }
        else if (regionIds.length > 1) {
            regionId = regionIds.reduce((acc, regionId) => {
                return regionsForThisCountry[regionId] > regionsForThisCountry[acc] ? regionId : acc;
            }, 0)

            debugRun(({ warn }) => {
                const regionNames = regionIds.map(regionId => getTaxonomyName(taxonomies, 'regions', regionId));

                warn(`Multiple regions (${
                    regionNames.join(', ')
                }) for country: ${
                    countryTaxonomy.name
                } (id=${
                    countryTaxonomy.id
                }). Using region: ${
                    getTaxonomyName(taxonomies, 'regions', regionId)
                } (id=${
                    regionId
                })`);
            });
        }

        if (regionId === 0) {
            debugRun(({ warn }) => {
                warn(`No region found for country taxonomy: ${
                    countryTaxonomy.name
                } (id=${
                    countryTaxonomy.id
                }).`);
            });
        }

        return {
            ...countryTaxonomy,
            hasLocalLevelData: !!profileIds.localLevel[countryTaxonomy.id],
            hasNationalLevelData: !!profileIds.nationalLevel[countryTaxonomy.id],
            regionId,
        };
    });
};

export const getTaxonomyValidation = (
    taxonomyType: keyof Taxonomies,
    allTaxonomies: Taxonomies,
    taxonomies: Taxonomy[],
    selectedIds: number[]
) => {
    const missingTaxonomyIds: number[] = [];

    const taxonomyListToUse = allTaxonomies[taxonomyType].filter(taxonomy => {
        const isValid = taxonomies.some(ls => ls.id === taxonomy.id);
        const isInvalidSelected = !isValid && selectedIds.includes(taxonomy.id);

        if (isInvalidSelected) {
            missingTaxonomyIds.push(taxonomy.id);
        }

        return isValid || isInvalidSelected;
    });

    return [taxonomyListToUse, missingTaxonomyIds] as const;
};
